/* (c) Dalineage, s.r.o. 2020-2025, all rights reserved */
package com.dalineage.client.objectbrowser

import org.scalajs.dom
import org.scalajs.dom.html
import org.scalajs.dom.document

import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global

import com.dalineage.client
import client.DomOps
import client.PropertyPanel

import com.dalineage.common

import com.dalineage.client.treecomponent
import treecomponent.TreeExplorer
import common.adt.TreeNodeADT.TreeNode
import treecomponent.TreeViewUserAction
import client.adt.PropertyPanelADT.KeyValue
import common.adt.PropertyPanelADT.PropertyName
import client.adt.PropertyPanelADT.Property
import io.circe._, io.circe.parser._, io.circe.syntax._

trait ObjectBrowserUserAction
case class ShowCode(node: TreeNode) extends ObjectBrowserUserAction
case class ShowDiagram(node: TreeNode) extends ObjectBrowserUserAction
case class SwitchFocus() extends ObjectBrowserUserAction
case class KeyboardEvent(event: dom.KeyboardEvent) extends ObjectBrowserUserAction

object ObjectBrowser {

  def init(
    contentDiv: html.Div,
    user: String,
    id: String,
    objectBrowserUserActionFn: ObjectBrowserUserAction => Future[Unit],
    initialKey: Option[List[String]] = None): Unit = {
    contentDiv.id = "tree-container"
    contentDiv.style.overflowY = "auto"

    def loadChildrenFn(key: String): Future[Seq[TreeNode]] =
      val keyenc = java.net.URLEncoder.encode(s"$key", "UTF-8")
      val url = s"web/objectbrowser/$user/$id/$keyenc"
      val promise = scala.concurrent.Promise[Seq[TreeNode]]()
      DomOps.getJsonObject[TreeNode](url, { treeNode =>
        println(s"Children loaded: ${treeNode.children}")
        promise.success(
          treeNode
            .children
            .getOrElse(Seq()))
      })
      promise.future

    def treeViewUserAction(action: TreeViewUserAction): Future[Unit] = action match
      case treecomponent.NodeSelected(node) =>
        println(s"Node selected: ${node.caption}")
        // Perform any action needed when a node is selected.
        val props: Seq[Property] = ObjectBrowserContextMenu.menu(node.key, node)
        Future{
          PropertyPanel.setProperties(props)
        }
      case treecomponent.NodeOpened(node, ctrl) =>
        Future{
          println(s"User action: $action")
          ctrl match
            case false => objectBrowserUserActionFn(ShowCode(node))
            case true => objectBrowserUserActionFn(ShowDiagram(node))
        }
      case treecomponent.KeyboardEvent(ev) if ev.keyCode == 9 =>
        objectBrowserUserActionFn(SwitchFocus())
      case treecomponent.KeyboardEvent(ev) =>
        // println(s"ObjectBrowserUserAction.KeyboardEvent ${ev.key}")
        objectBrowserUserActionFn(KeyboardEvent(ev))

    // Add some basic CSS for styling.
    val style = document.createElement("style")
    style.textContent =
      """
        |.icon {
        |  height: 19px;
        |  align-items: center;
        |  margin-right: 5px;
        |}
        |.tree-node {
        |  padding-left: 12px;
        |  white-space: nowrap;
        |  font-family: Arial, sans-serif;
        |  font-size: 15px;
        |}
        |.node-header {
        |  cursor: pointer;
        |  padding: 1px 4px; /* Reduced from 2px */
        |}
        |.node-header.selected {
        |  background-color: lightblue;
        |}
        |.toggle-button {
        |  display: inline-block;
        |  width: 12px;
        |  height: 12px;
        |  line-height: 12px;
        |  font-size: 12px;
        |  text-align: center;
        |  border: 1px solid black;
        |  margin-right: 5px;
        |  cursor: pointer;
        |}
        |.toggle-spacer {
        |  display: inline-block;
        |  width: 16px;
        |  margin-right: 5px;
        |}
        |.children-container {
        |  margin-left: 3px;
        |}
        |""".stripMargin
    document.head.appendChild(style)

    // Initialize the tree explorer.
    DomOps.getJsonObject[TreeNode](
      s"web/objectbrowser/$user/$id",
      { rootNode =>
        TreeExplorer.init(rootNode, loadChildrenFn, treeViewUserAction, initialKey)
      }
    )
  }
}
